import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { industryByKey, daysSince, PRIORITY_COLORS } from "../lib/constants";
import IndustryBadge from "../components/IndustryBadge";
import { Plus, CaretUp, CaretDown } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const COLS = [
  { key: "project_code", label: "Project ID", mono: true },
  { key: "client_name", label: "Client" },
  { key: "industry", label: "Industry" },
  { key: "product_summary", label: "Product Summary" },
  { key: "stage", label: "Status" },
  { key: "engineer_name", label: "Engineer" },
  { key: "created_at", label: "Created", mono: true },
  { key: "days", label: "Days Active", mono: true },
];

export default function Projects() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });
  const [filters, setFilters] = useState({ industry: "all", stage: "all", client_id: "all", engineer_id: "all" });
  const [viewMode, setViewMode] = useState("list"); // list | kanban
  const [loading, setLoading] = useState(true);
  const stages = user?.org?.workflow_stages || [];
  const orgIndustries = user?.org?.industries || [];

  // SLA: % of stage sla_days consumed since stage_entered_at
  const slaOf = (p) => {
    const st = stages.find((s) => s.key === p.stage);
    if (!st || !st.sla_days || !p.stage_entered_at) return null;
    const elapsed = (Date.now() - new Date(p.stage_entered_at).getTime()) / 86400000;
    const pct = elapsed / st.sla_days;
    return { pct, state: pct > 1 ? "overdue" : pct > 0.9 ? "red" : pct > 0.6 ? "amber" : "green" };
  };
  const SLA_COLORS = { green: "var(--c-green)", amber: "var(--c-amber)", red: "var(--c-red)", overdue: "var(--c-red)" };

  const load = async () => {
    try {
      const [p, c, u] = await Promise.all([api.get("/projects"), api.get("/clients"), api.get("/users")]);
      setProjects(p.data); setClients(c.data); setUsers(u.data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stageName = (key) => stages.find((s) => s.key === key)?.name || key;

  const rows = useMemo(() => {
    let r = projects.filter((p) =>
      (filters.industry === "all" || p.industry === filters.industry) &&
      (filters.stage === "all" || p.stage === filters.stage) &&
      (filters.client_id === "all" || p.client_id === filters.client_id) &&
      (filters.engineer_id === "all" || p.engineer_id === filters.engineer_id)
    );
    const { key, dir } = sort;
    r = [...r].sort((a, b) => {
      const va = key === "days" ? daysSince(a.created_at) : (a[key] || "");
      const vb = key === "days" ? daysSince(b.created_at) : (b[key] || "");
      return (va > vb ? 1 : va < vb ? -1 : 0) * dir;
    });
    return r;
  }, [projects, filters, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const FilterSelect = ({ k, options, allLabel, testid }) => (
    <Select value={filters[k]} onValueChange={(v) => setFilters((f) => ({ ...f, [k]: v }))}>
      <SelectTrigger data-testid={testid} className="w-44 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
        <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-6" data-testid="projects-page">
      <div className="flex items-center gap-3 flex-wrap">
        <FilterSelect k="industry" testid="filter-industry" allLabel="All Industries"
          options={orgIndustries.map((k) => ({ value: k, label: industryByKey(k).name }))} />
        <FilterSelect k="stage" testid="filter-status" allLabel="All Statuses"
          options={stages.map((s) => ({ value: s.key, label: s.name }))} />
        <FilterSelect k="client_id" testid="filter-client" allLabel="All Clients"
          options={clients.map((c) => ({ value: c.id, label: c.name }))} />
        <FilterSelect k="engineer_id" testid="filter-engineer" allLabel="All Engineers"
          options={users.map((u) => ({ value: u.id, label: u.name }))} />
        <div className="flex-1" />
        <div className="flex gap-1 mr-2" data-testid="view-toggle">
          {[["list", "☰ List"], ["kanban", "▦ Kanban"]].map(([m, l]) => (
            <button key={m} data-testid={`view-${m}`} onClick={() => setViewMode(m)}
              className={`h-8 px-3 text-[11px] rounded-sm border transition-colors ${
                viewMode === m ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)]/60 text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
              {l}
            </button>
          ))}
        </div>
        <Button data-testid="new-project-btn" onClick={() => nav("/projects/new")}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
          <Plus size={14} className="mr-1" /> New Project
        </Button>
      </div>

      {viewMode === "list" && (
      <div className="mt-4 border border-[var(--c-border)] rounded-sm bg-[var(--c-surface)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead>
            <tr className="border-b border-[var(--c-border)]">
              {COLS.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}
                  className="text-left px-4 py-3 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] cursor-pointer hover:text-[var(--c-text)] select-none whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sort.key === c.key && (sort.dir === 1 ? <CaretUp size={10} /> : <CaretDown size={10} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.project_code}`} onClick={() => nav(`/projects/${p.id}`)}
                className="border-b border-[var(--c-border)]/60 last:border-0 hover:bg-[var(--c-border)]/25 cursor-pointer transition-colors duration-150">
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)] whitespace-nowrap">{p.project_code}</td>
                <td className="px-4 py-3 text-xs text-[var(--c-text)] whitespace-nowrap">{p.client_name}</td>
                <td className="px-4 py-3"><IndustryBadge industryKey={p.industry} /></td>
                <td className="px-4 py-3 text-xs text-[var(--c-text)] max-w-64 truncate">{p.product_summary}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] whitespace-nowrap">{stageName(p.stage)}</span>
                    {(() => { const s = slaOf(p); return s && (
                      <span data-testid={`sla-${p.project_code}`} title={`SLA: ${Math.round(s.pct * 100)}% of stage SLA used`}
                        className="w-2 h-2 rounded-full" style={{ backgroundColor: SLA_COLORS[s.state] }} />); })()}
                    {p.ready_to_advance && <span data-testid={`ready-${p.project_code}`} title="Ready to advance" className="text-[10px] text-[var(--c-green)]">✅</span>}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--c-muted)] whitespace-nowrap">{p.engineer_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-muted)] whitespace-nowrap">{String(p.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--c-text)]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[p.priority] }} />
                    {daysSince(p.created_at)}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[var(--c-muted)]"
                data-testid={loading ? "projects-loading" : "projects-empty"}>
                {loading ? "Loading projects…" : "No projects match the filters."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === "kanban" && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-4" data-testid="projects-kanban">
          {stages.map((st) => {
            const col = rows.filter((p) => p.stage === st.key);
            return (
              <div key={st.key} className="w-64 shrink-0 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid={`kanban-col-${st.key}`}>
                <div className="px-3 py-2 border-b border-[var(--c-border)] flex items-center gap-2">
                  <span className="text-[11px] font-medium text-[var(--c-text)]">{st.name}</span>
                  <span className="font-mono text-[9px] text-[var(--c-muted)]">{col.length}</span>
                  {st.sla_days > 0 && <span className="ml-auto font-mono text-[9px] text-[var(--c-muted)]">SLA {st.sla_days}d</span>}
                </div>
                <div className="p-2 space-y-2 max-h-[520px] overflow-y-auto">
                  {col.map((p) => {
                    const sla = slaOf(p);
                    return (
                      <div key={p.id} onClick={() => nav(`/projects/${p.id}`)} data-testid={`kanban-card-${p.project_code}`}
                        className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-2.5 cursor-pointer hover:border-[var(--c-accent)]/50 transition-colors">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] text-[var(--c-accent)]">{p.project_code}</span>
                          {p.ready_to_advance && <span className="text-[10px]" title="Ready to advance">✅</span>}
                          <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[p.priority] }} />
                        </div>
                        <div className="text-[11px] text-[var(--c-text)] mt-1 leading-snug">{p.product_summary}</div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[9px] text-[var(--c-muted)] truncate">{p.client_name}</span>
                          <span className="ml-auto font-mono text-[9px] text-[var(--c-muted)]">{p.engineer_name?.split(" ")[0]}</span>
                        </div>
                        {sla && (
                          <div className="mt-1.5">
                            <div className="h-1 bg-[var(--c-border)] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" data-testid={`kanban-sla-${p.project_code}`}
                                style={{ width: `${Math.min(100, sla.pct * 100)}%`, background: SLA_COLORS[sla.state] }} />
                            </div>
                            <div className="font-mono text-[8px] mt-0.5" style={{ color: SLA_COLORS[sla.state] }}>
                              {sla.state === "overdue" ? "SLA OVERDUE" : `${Math.round(sla.pct * 100)}% of SLA`}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!col.length && <div className="text-[10px] text-[var(--c-muted)] text-center py-4">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
